import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMyPeopleEvalData } from './hooks/useMyPeopleEvalData';
import { MyTeamEvalReport } from '@modules/EvaluationModule/components/MyTeamEvalReport/MyTeamEvalReport';
import { MyPeopleEvalReportTable } from '@modules/EvaluationModule/components/MyTeamEvalReport/components/MyPeopleEvalReportTable/MyPeopleEvalReportTable';
import { DataState } from '@shared/enums/DataState';
import { getMyPeopleEvalData, setManagersIds, setMembersIds } from './redux/actions';
import {
  setManagersData,
  setMembersData,
  setManagersLoading,
  setMembersLoading,
  setAllLocationsDataLoading,
  setSortInfo,
} from './redux';
import { MY_PEOPLE_EVALS_FETCH_LIMIT, DEFAULT_PAGINATION_DATA } from './constants/pagination';
import { MyPeopleEvalFilters } from './components';
import { MyPeopleEvalDataFilters } from './interfaces/MyPeopleEvalsDataFilters';
import { EvalStatuses } from './enums/EvalStatuses';
import { EvalSortFields } from './enums/EvalSortFields';
import { EvalFilterFields } from './enums/EvalFilterFields';
import { EvalFiltersFields } from './interfaces/EvalFiltersFields';
import { SortInfo } from '@shared/interfaces/sortInfo';
import { SortDirection } from '@shared/enums/sortDirection';
import { EmploymentType } from '@shared/enums/EmploymentType';
import { EngagementModel } from '@modules/EvaluationModule/enums/EngagementModel';
import { useAllDepartments } from '@shared/hooks/useAllDepartments';
import {
  selectManagersData,
  selectMembersData,
  selectManagersState,
  selectMembersState,
  selectSortInfo,
  selectMyPeopleEvalTotal,
} from './redux/selectors';
import { idToUser } from '@shared/helpers/idToUser';
import { commaToArrayParser } from '@shared/helpers/commaToArrayParser';
import { projectIdsToName } from '@shared/helpers/projectIdsToName';
import { convertToBackendFilters } from '@modules/EvaluationModule/helpers/filterEvaluationsByStatus';
import { extendEvalStateSelector } from '@modules/EvaluationModule/pages/MyTeamEvalPage/redux/selectors';

export const MyPeopleEvalPage: React.FC = () => {
  const dispatch = useDispatch();
  const { myPeopleEvalData, locationOptions: locations } = useMyPeopleEvalData();
  const isDataLoading = myPeopleEvalData.state === DataState.Pending;
  const dataLength = myPeopleEvalData.data ? myPeopleEvalData.data.length : 0;
  const [searchParams] = useSearchParams();

  const allDepartments = useAllDepartments();
  const managersData = useSelector(selectManagersData) ?? [];
  const membersData = useSelector(selectMembersData) ?? [];
  const sortInfo = useSelector(selectSortInfo);
  const extendEvalState = useSelector(extendEvalStateSelector);
  const myPeopleEvalTotal = useSelector(selectMyPeopleEvalTotal);
  const managersState = useSelector(selectManagersState);
  const membersState = useSelector(selectMembersState);

  const isFilterDataLoading =
    allDepartments.state === DataState.Pending ||
    managersState === DataState.Pending ||
    membersState === DataState.Pending;

  const statusFromParams = searchParams.get(EvalFilterFields.Status);
  const DEFAULT_EVAL_STATUSES = [EvalStatuses.Upcoming, EvalStatuses.Ongoing, EvalStatuses.PendingToClose, EvalStatuses.PendingToStart];
  const statusArray = statusFromParams ? (statusFromParams.split(',') as EvalStatuses[]) : DEFAULT_EVAL_STATUSES;
  const locationsFromParams = searchParams.get(EvalFilterFields.Locations);
  const managersFromParams = searchParams.get(EvalFilterFields.Managers);
  const membersFromParams = searchParams.get(EvalFilterFields.Members);
  const departmentsFromParams = searchParams.get(EvalFilterFields.Departments);
  const eopStatusFromParams = searchParams.get(EvalFilterFields.EopStatus);
  const dateFromParams = searchParams.get(EvalFilterFields.DateFrom);
  const dateToParams = searchParams.get(EvalFilterFields.DateTo);
  const employmentTypeFromParams = searchParams.get(EvalFilterFields.EmploymentType);
  const hasOngoingFlagFromParams = searchParams.get(EvalFilterFields.HasOngoingFlag) === 'true';
  const engagementModelsFromParams = searchParams.get(EvalFilterFields.EngagementModels);
  const includeRegularFromParams = searchParams.get(EvalFilterFields.IncludeRegular) !== 'false';
  const includeEopFromParams = searchParams.get(EvalFilterFields.IncludeEop) !== 'false';
  const managerIds = managersFromParams?.split(',');
  const memberIds = membersFromParams?.split(',');

  const initialValues: EvalFiltersFields = {
    status: statusArray,
    locations: locationsFromParams ? locationsFromParams.split(',') : [],
    managers: idToUser(managersData, managersFromParams),
    members: idToUser(membersData, membersFromParams),
    dateFrom: dateFromParams,
    dateTo: dateToParams,
    departments: departmentsFromParams
      ? projectIdsToName(departmentsFromParams.split(','), allDepartments.data ?? [])
      : [],
    includeRegular: includeRegularFromParams,
    includeEop: includeEopFromParams,
    eopStatus: eopStatusFromParams,
    isRescheduled: Boolean(searchParams.get(EvalFilterFields.IsRescheduled)) ?? false,
    eopResolvedAtFrom: searchParams.get(EvalFilterFields.EopResolvedAtFrom),
    eopResolvedAtTo: searchParams.get(EvalFilterFields.EopResolvedAtTo),
    eopExtendedToFrom: searchParams.get(EvalFilterFields.EopExtendedToFrom),
    eopExtendedToTo: searchParams.get(EvalFilterFields.EopExtendedToTo),
    employmentType: (employmentTypeFromParams as EmploymentType) || null,
    hasOngoingFlag: hasOngoingFlagFromParams,
    engagementModels: engagementModelsFromParams ? (engagementModelsFromParams.split(',') as EngagementModel[]) : [],
  };

  useEffect(() => {
    if (managerIds) {
      dispatch(setManagersIds(managerIds));
    } else {
      dispatch(setManagersData({ data: undefined, state: DataState.Fulfilled }));
    }

    if (memberIds) {
      dispatch(setMembersIds(memberIds));
    } else {
      dispatch(setMembersData({ data: undefined, state: DataState.Fulfilled }));
    }

    return () => {
      dispatch(setAllLocationsDataLoading());
      dispatch(setManagersLoading());
      dispatch(setMembersLoading());
    };
  }, []);

  const getFilterPayload = (overrides?: Partial<MyPeopleEvalDataFilters>): MyPeopleEvalDataFilters => {
    const limit = overrides?.limit ?? MY_PEOPLE_EVALS_FETCH_LIMIT;
    const offset = overrides?.offset ?? DEFAULT_PAGINATION_DATA.offset;

    const payload: MyPeopleEvalDataFilters = {
      limit,
      offset,
      status: convertToBackendFilters(statusArray),
      dateFrom: dateFromParams,
      dateTo: dateToParams,
      locations: commaToArrayParser(locationsFromParams),
      managers: commaToArrayParser(managersFromParams),
      members: commaToArrayParser(membersFromParams),
      departments: commaToArrayParser(departmentsFromParams),
      includeRegular: includeRegularFromParams,
      includeEop: includeEopFromParams,
      eopStatus: eopStatusFromParams,
      sortInfo: sortInfo ?? { field: EvalSortFields.Date, sortDirection: SortDirection.Desc },
      eopResolvedAtFrom: searchParams.get(EvalFilterFields.EopResolvedAtFrom),
      eopResolvedAtTo: searchParams.get(EvalFilterFields.EopResolvedAtTo),
      eopExtendedToFrom: searchParams.get(EvalFilterFields.EopExtendedToFrom),
      eopExtendedToTo: searchParams.get(EvalFilterFields.EopExtendedToTo),
      employmentType: employmentTypeFromParams ? [employmentTypeFromParams as EmploymentType] : undefined,
      hasOngoingFlag: hasOngoingFlagFromParams,
      engagementModels: commaToArrayParser(engagementModelsFromParams) as EngagementModel[] | undefined,
    };

    return { ...payload, ...overrides };
  };

  useEffect(() => {
    dispatch(getMyPeopleEvalData(getFilterPayload()));
  }, [searchParams, sortInfo]);

  useEffect(() => {
    if (extendEvalState === DataState.Fulfilled) {
      dispatch(getMyPeopleEvalData(getFilterPayload()));
    }
  }, [extendEvalState]);

  const getMoreData = useCallback(
    (offset = 0) => {
      dispatch(getMyPeopleEvalData(getFilterPayload({ offset })));
    },
    [searchParams, sortInfo]
  );

  const loadMoreItems = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (myPeopleEvalData && stopIndex > dataLength) {
        getMoreData && getMoreData(startIndex);
      }
    },
    [myPeopleEvalData, getMoreData, dataLength]
  );

  const handleSort = useCallback((sortInfo: SortInfo) => {
    dispatch(setSortInfo(sortInfo));
  }, []);

  return (
    <MyTeamEvalReport
      statusFilter={statusArray}
      tableTitle="My people evaluations"
      myTeamEvalData={myPeopleEvalData}
      count={myPeopleEvalTotal}
      sideBlock={
        <MyPeopleEvalFilters
          departments={allDepartments.data ?? []}
          locations={locations}
          isLoading={isDataLoading}
          isFilterLoading={isFilterDataLoading}
          initialValues={initialValues}
        />
      }
    >
      <MyPeopleEvalReportTable
        data={myPeopleEvalData.data}
        isDataLoading={isDataLoading}
        loadMoreItems={loadMoreItems}
        onSort={handleSort}
        sortInfo={getFilterPayload().sortInfo}
      ></MyPeopleEvalReportTable>
    </MyTeamEvalReport>
  );
};
